/**
 * «الإعدادات والأمان»'s own tabs (المرحلة 1.1, F08 and P24).
 *
 *   ProfileTab      the person's details; a new email or mobile becomes theirs
 *                   only once a code sent to it is typed back
 *   PreferencesTab  the language, and which topics reach them by which channel
 *   SessionsTab     every device signed in, each one ended on its own or all
 *   ConsentsTab     the legal texts in force and which version they accepted
 *
 * Every rule (may the email be changed here, which email domain, the country's
 * mobile format, how long a session lasts) is the account's scope's policy —
 * read from GET /api/auth/me and enforced again by the server.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, ConfirmDialog, DateInput, EmptyState, ErrorState, Field, Icon, Input, Panel, SectionTitle, Spinner } from '../../components/common';
import { authApi } from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useI18n } from '../../i18n/I18nProvider';
import { formatDate, formatDateTime } from '../../domain/locale';
import { CodeBoxes } from '../auth/ForgotPasswordPage';

/* ------------------------------------------------------------------------ */
/* بياناتي                                                                    */
/* ------------------------------------------------------------------------ */

const ageOf = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  if (now < new Date(now.getFullYear(), birth.getMonth(), birth.getDate())) years -= 1;
  return years >= 0 ? years : null;
};

export function ProfileTab({ me, onChanged }) {
  const { t, lang } = useI18n();
  const { updateSession } = useAuth();
  const { success, error: toastError } = useNotifications();
  const [form, setForm] = useState({ name: me.name || '', dateOfBirth: me.dateOfBirth || '', gender: me.gender || '' });
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState({});

  useEffect(() => {
    setForm({ name: me.name || '', dateOfBirth: me.dateOfBirth || '', gender: me.gender || '' });
  }, [me.name, me.dateOfBirth, me.gender]);

  const dirty = form.name !== (me.name || '') || form.dateOfBirth !== (me.dateOfBirth || '') || form.gender !== (me.gender || '');
  const age = ageOf(form.dateOfBirth);
  const country = me.country || {};

  const save = async () => {
    setSaving(true);
    setFieldError({});
    try {
      await authApi.updateProfile({ name: form.name, dateOfBirth: form.dateOfBirth || null, gender: form.gender || null });
      updateSession({ name: form.name.trim() });
      success(t('profile.saved'));
      onChanged();
    } catch (err) {
      if (err.body?.field) setFieldError({ [err.body.field]: err.message });
      else toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <Panel>
        <SectionTitle icon="user-round">{t('profile.title')}</SectionTitle>
        <p className="-mt-2 mb-5 text-sm leading-relaxed text-slate-400">{t('profile.intro')}</p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label={t('profile.name')} required error={fieldError.name}>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="profile-name" />
          </Field>
          <Field label={t('profile.username')}>
            <Input value={me.username || ''} disabled dir="ltr" className="opacity-70" />
          </Field>
          <Field label={t('profile.dob')} error={fieldError.dateOfBirth} hint={age !== null ? t('profile.age', { n: age }) : undefined}>
            <DateInput value={form.dateOfBirth || ''} onChange={(e) => setForm((f) => ({ ...f, dateOfBirth: e.target.value }))} />
          </Field>
          <Field label={t('profile.gender')}>
            <div className="choice-cards">
              {[
                ['male', 'profile.male', 'user-round'],
                ['female', 'profile.female', 'user-round'],
                ['', 'profile.unspecified', 'circle-dashed'],
              ].map(([value, label, icon]) => (
                <button key={label} type="button" className={`choice-card !py-2.5 ${form.gender === value ? 'is-on' : ''}`} onClick={() => setForm((f) => ({ ...f, gender: value }))} aria-pressed={form.gender === value}>
                  <Icon name={icon} size={15} className={value === 'female' ? 'text-rose-400' : value === 'male' ? 'text-cyan-400' : 'text-slate-400'} />
                  {t(label)}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
          <p className="text-xs text-slate-500">{t('profile.memberSince', { when: formatDate(me.createdAt) })}</p>
          <Button icon="save" onClick={save} loading={saving} disabled={!dirty} data-testid="profile-save">
            {t('common.save')}
          </Button>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ContactCard channel="email" me={me} onChanged={onChanged} />
        <ContactCard channel="phone" me={me} onChanged={onChanged} />
      </div>

      {/* The country this account's institution runs in: its money and its clock */}
      <Panel className="country-card">
        <div className="flex flex-wrap items-center gap-4">
          <span className="country-card__flag" aria-hidden="true">
            {country.code === 'SA' ? '🇸🇦' : country.code === 'AE' ? '🇦🇪' : '🇪🇬'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-slate-400">{t('profile.country')}</p>
            <p className="text-lg font-black text-white">{lang === 'en' ? country.nameEn : country.name}</p>
          </div>
          <div className="country-card__facts">
            <span>
              <Icon name="banknote" size={14} />
              {t('profile.currency')}: <b>{lang === 'en' ? country.currencySymbolEn || country.currency : country.currencySymbol}</b>
            </span>
            <span>
              <Icon name="clock-3" size={14} />
              {t('profile.timezone')}: <b>{lang === 'en' ? country.timezoneLabelEn : country.timezoneLabel}</b>
            </span>
            <span dir="ltr">
              <Icon name="smartphone" size={14} />+{country.dialCode}
            </span>
          </div>
        </div>
      </Panel>
    </div>
  );
}

/** The email or the mobile: shown, verified or not, and changed by a code. */
function ContactCard({ channel, me, onChanged }) {
  const { t } = useI18n();
  const { success } = useNotifications();
  const isEmail = channel === 'email';
  const value = isEmail ? me.email : me.phone;
  const verified = isEmail ? me.emailVerifiedAt : me.phoneVerifiedAt;
  const locked = me.rules?.contactEdit === 'admin';

  const [mode, setMode] = useState('view');
  const [draft, setDraft] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setMode('view');
    setDraft('');
    setCode('');
    setError(null);
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await authApi.contactStart(channel, draft.trim());
      setSentTo(answer);
      setMode('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await authApi.contactConfirm(channel, code);
      success(isEmail ? t('profile.emailChanged') : t('profile.phoneChanged'));
      reset();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel className="contact-card">
      <div className="flex items-start gap-3">
        <span className={`tile3d__face ${isEmail ? 'tone-cyan' : 'tone-emerald'} shrink-0`} style={{ width: 44, height: 44 }}>
          <Icon name={isEmail ? 'mail' : 'smartphone'} size={20} className="tile3d__icon" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-400">{isEmail ? t('profile.email') : t('profile.phone')}</p>
          <p className="truncate text-base font-bold text-white" dir="ltr" style={{ textAlign: 'start' }}>
            {value || '—'}
          </p>
          {value && (
            <span className={`contact-card__state ${verified ? 'is-ok' : ''}`}>
              <Icon name={verified ? 'badge-check' : 'circle-dashed'} size={13} />
              {verified ? t('profile.verified') : t('profile.notVerified')}
            </span>
          )}
        </div>
        {mode === 'view' && !locked && (
          <Button variant="subtle" icon="pencil" onClick={() => setMode('edit')} data-testid={`contact-edit-${channel}`}>
            {t('profile.change')}
          </Button>
        )}
      </div>

      {locked && mode === 'view' && <p className="mt-3 text-xs text-amber-300">{t('profile.adminOnly')}</p>}

      {mode === 'edit' && (
        <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
          <Field label={isEmail ? t('profile.newEmail') : t('profile.newPhone')}>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              dir="ltr"
              type={isEmail ? 'email' : 'tel'}
              placeholder={isEmail ? 'name@example.com' : me.country?.mobile?.example}
              autoFocus
              data-testid={`contact-value-${channel}`}
            />
          </Field>
          {error && <p className="text-xs font-bold text-red-400">{error}</p>}
          <div className="flex gap-2">
            <Button icon="send" onClick={start} loading={busy} disabled={!draft.trim()}>
              {t('profile.sendCode')}
            </Button>
            <Button variant="ghost" onClick={reset}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {mode === 'code' && (
        <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
          <p className="text-xs leading-relaxed text-slate-300">{t('profile.codeSent', { to: sentTo?.destination || draft, n: sentTo?.minutes || 15 })}</p>
          <CodeBoxes value={code} onChange={setCode} autoFocus label={t('profile.enterCode')} />
          {error && <p className="text-center text-xs font-bold text-red-400">{error}</p>}
          <div className="flex justify-center gap-2">
            <Button icon="badge-check" onClick={confirm} loading={busy} disabled={code.length !== 6}>
              {t('profile.confirm')}
            </Button>
            <Button variant="ghost" onClick={reset}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------------ */
/* التفضيلات                                                                  */
/* ------------------------------------------------------------------------ */

const TOPICS = [
  { key: 'appointments', icon: 'calendar-clock', tone: 'cyan' },
  { key: 'results', icon: 'flask-conical', tone: 'purple' },
  { key: 'offers', icon: 'percent', tone: 'amber' },
];
const CHANNELS = [
  { key: 'app', icon: 'bell' },
  { key: 'sms', icon: 'message-square' },
  { key: 'email', icon: 'mail' },
];

/** What a person who never chose gets — what everybody got before this screen. */
export const DEFAULT_PREFS = Object.freeze({
  appointments: { app: true, sms: true, email: true },
  results: { app: true, sms: true, email: true },
  offers: { app: true, sms: true, email: true },
});

export function PreferencesTab({ me, onChanged }) {
  const { t, lang, setLang, canSwitch, languages } = useI18n();
  const { success, error: toastError } = useNotifications();
  const [prefs, setPrefs] = useState(() => me.notificationPrefs || DEFAULT_PREFS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPrefs(me.notificationPrefs || DEFAULT_PREFS);
  }, [me.notificationPrefs]);

  const toggle = (topic, channel) =>
    setPrefs((current) => ({ ...current, [topic]: { ...current[topic], [channel]: !current[topic]?.[channel] } }));

  const dirty = JSON.stringify(prefs) !== JSON.stringify(me.notificationPrefs || DEFAULT_PREFS);

  const save = async () => {
    setSaving(true);
    try {
      await authApi.updateProfile({ notificationPrefs: prefs });
      success(t('prefs.saved'));
      onChanged();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <Panel>
        <SectionTitle icon="languages">{t('prefs.language')}</SectionTitle>
        <p className="-mt-2 mb-4 text-sm text-slate-400">{canSwitch ? t('prefs.languageHint') : t('prefs.languageLocked', { lang: t(`lang.${languages[0]}`) })}</p>
        <div className="lang-cards">
          {['ar', 'en'].map((code) => {
            const offered = languages.includes(code);
            return (
              <button
                key={code}
                type="button"
                disabled={!offered}
                onClick={() => setLang(code)}
                className={`lang-card ${lang === code ? 'is-on' : ''}`}
                aria-pressed={lang === code}
                data-testid={`pref-lang-${code}`}
              >
                <span className="lang-card__glyph" lang={code}>
                  {code === 'ar' ? 'ع' : 'En'}
                </span>
                <span className="min-w-0 flex-1 text-start">
                  <span className="block font-black text-white">{code === 'ar' ? 'العربية' : 'English'}</span>
                  <span className="block text-[11px] text-slate-400">{code === 'ar' ? 'من اليمين لليسار' : 'Left to right'}</span>
                </span>
                {lang === code && <Icon name="circle-check" size={20} className="text-emerald-400" />}
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel>
        <SectionTitle icon="bell">{t('prefs.notifications')}</SectionTitle>
        <p className="-mt-2 mb-4 text-sm text-slate-400">{t('prefs.notificationsHint')}</p>
        <div className="prefs-grid" role="table">
          <div className="prefs-grid__row prefs-grid__head" role="row">
            <span role="columnheader" />
            {CHANNELS.map((channel) => (
              <span key={channel.key} role="columnheader" className="prefs-grid__ch">
                <Icon name={channel.icon} size={15} />
                {t(channel.key === 'app' ? 'prefs.app' : channel.key === 'sms' ? 'prefs.sms' : 'prefs.emailCh')}
              </span>
            ))}
          </div>
          {TOPICS.map((topic) => (
            <div key={topic.key} className="prefs-grid__row" role="row">
              <span role="rowheader" className="flex items-center gap-3">
                <span className={`tile3d__face tone-${topic.tone} shrink-0`} style={{ width: 38, height: 38 }}>
                  <Icon name={topic.icon} size={17} className="tile3d__icon" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-white">{t(`prefs.${topic.key}`)}</span>
                  <span className="block text-[11px] leading-snug text-slate-400">{t(`prefs.${topic.key}Hint`)}</span>
                </span>
              </span>
              {CHANNELS.map((channel) => {
                const on = prefs[topic.key]?.[channel.key] !== false;
                return (
                  <span key={channel.key} role="cell" className="prefs-grid__cell">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={`${t(`prefs.${topic.key}`)} — ${channel.key}`}
                      className={`switch ${on ? 'is-on' : ''}`}
                      onClick={() => toggle(topic.key, channel.key)}
                      data-testid={`pref-${topic.key}-${channel.key}`}
                    >
                      <span className="switch__knob" />
                    </button>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end border-t border-white/10 pt-4">
          <Button icon="save" onClick={save} loading={saving} disabled={!dirty} data-testid="prefs-save">
            {t('common.save')}
          </Button>
        </div>
      </Panel>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* الأجهزة والجلسات                                                           */
/* ------------------------------------------------------------------------ */

function deviceIcon(device) {
  const text = String(device || '');
  if (/iPhone|Android/.test(text)) return 'smartphone';
  if (/iPad/.test(text)) return 'tablet-smartphone';
  if (/API/.test(text)) return 'code';
  if (/Windows|macOS|Linux/.test(text)) return 'laptop';
  return 'monitor-smartphone';
}

const METHOD_KEYS = { password: 'sessions.viaPassword', google: 'sessions.viaGoogle', reset: 'sessions.viaReset' };

export function SessionsTab({ me }) {
  const { t } = useI18n();
  const { logout } = useAuth();
  const { success, error: toastError } = useNotifications();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const answer = await authApi.sessions();
      const list = (answer.sessions || []).slice().sort((a, b) => Number(b.current) - Number(a.current));
      setRows(list);
    } catch (err) {
      setLoadError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const endOne = async (row) => {
    setBusy(row.id);
    try {
      await authApi.endSession(row.id);
      success(t('sessions.endedOne'));
      await load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const endOthers = async () => {
    setBusy('others');
    try {
      const answer = await authApi.endAllSessions(true);
      success(t('sessions.endedOthers', { n: answer.ended }));
      await load();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const endAll = async () => {
    setBusy('all');
    try {
      await authApi.endAllSessions(false);
      logout();
      navigate('/login', { replace: true, state: { notice: 'auth.signedOut' } });
    } catch (err) {
      toastError(err.message);
      setBusy(null);
    }
  };

  if (loadError) return <ErrorState error={loadError} onRetry={load} />;
  if (!rows) return <Spinner variant="inline" label={t('common.loading')} />;

  const others = rows.filter((row) => !row.current);

  return (
    <div className="space-y-5">
      <Panel>
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <SectionTitle icon="monitor-smartphone" className="!mb-1">
              {t('sessions.title')}
            </SectionTitle>
            <p className="max-w-2xl text-sm leading-relaxed text-slate-400">{t('sessions.intro')}</p>
          </div>
          <div className="sessions-count">
            <b>{rows.length}</b>
            <span>{t('sessions.devicesLabel')}</span>
          </div>
        </div>

        <ul className="space-y-3" data-testid="sessions-list">
          {rows.map((row) => (
            <li key={row.id} className={`session-row ${row.current ? 'is-current' : ''}`}>
              <span className={`tile3d__face ${row.current ? 'tone-emerald' : 'tone-slate'} shrink-0`} style={{ width: 46, height: 46 }}>
                <Icon name={deviceIcon(row.device)} size={21} className="tile3d__icon" />
              </span>
              <div className="session-row__info min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-bold text-white">
                  <span dir="ltr">{row.device || '—'}</span>
                  {row.current && (
                    <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="circle-check">
                      {t('sessions.current')}
                    </Badge>
                  )}
                  {METHOD_KEYS[row.method] && <span className="text-[11px] font-normal text-slate-400">· {t(METHOD_KEYS[row.method])}</span>}
                </p>
                <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                  <span>
                    <Icon name="clock-3" size={11} className="me-1 inline" />
                    {t('sessions.lastSeen', { when: formatDateTime(row.lastSeenAt || row.createdAt) })}
                  </span>
                  <span>{t('sessions.since', { when: formatDateTime(row.createdAt) })}</span>
                  {row.ip && (
                    <span dir="ltr" className="font-mono">
                      {row.ip}
                    </span>
                  )}
                </p>
              </div>
              {!row.current && (
                <Button variant="subtle" icon="log-out" className="session-row__end !text-red-300" loading={busy === row.id} onClick={() => endOne(row)} data-testid="session-end">
                  {t('sessions.signOutDevice')}
                </Button>
              )}
            </li>
          ))}
        </ul>
        {!others.length && <p className="mt-3 text-center text-xs text-slate-500">{t('sessions.none')}</p>}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
          <p className="text-xs text-slate-500">
            <Icon name="timer" size={12} className="me-1 inline" />
            {t('sessions.hours', { n: me.rules?.sessionHours || 24 })}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" icon="users" disabled={!others.length} loading={busy === 'others'} onClick={endOthers} data-testid="sessions-end-others">
              {t('sessions.signOutOthers')}
            </Button>
            <Button variant="danger" icon="shield-x" loading={busy === 'all'} onClick={() => setConfirmAll(true)}>
              {t('sessions.signOutAll')}
            </Button>
          </div>
        </div>
      </Panel>

      <ConfirmDialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        onConfirm={endAll}
        title={t('sessions.signOutAll')}
        message={t('sessions.confirmAll')}
        confirmLabel={t('sessions.signOutAll')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={busy === 'all'}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* الموافقات                                                                  */
/* ------------------------------------------------------------------------ */

const KIND_TONE = { terms: 'cyan', privacy: 'purple', telehealth: 'teal' };
const KIND_ICON = { terms: 'scale', privacy: 'lock-keyhole', telehealth: 'video' };

export function ConsentsTab() {
  const { t, lang } = useI18n();
  const { consentDone } = useAuth();
  const { success, error: toastError } = useNotifications();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setData(await authApi.consent());
    } catch (err) {
      setLoadError(err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) return <ErrorState error={loadError} onRetry={load} />;
  if (!data) return <Spinner variant="inline" label={t('common.loading')} />;
  if (!data.texts?.length) return <EmptyState icon="scale" title={t('settings.tabConsents')} />;

  const titleOf = (text) => (lang === 'en' && text.titleEn ? text.titleEn : text.title);
  const bodyOf = (text) => (lang === 'en' && text.bodyEn ? text.bodyEn : text.body);

  const accept = async (text) => {
    setBusy(text.id);
    try {
      await authApi.acceptConsent([text.id], 'settings');
      success(t('consent.acceptedOn', { when: formatDate(new Date()) }));
      await load();
      consentDone();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      {data.texts.map((text) => {
        const mine = data.accepted.find((row) => row.consentVersionId === text.id);
        const older = !mine && data.accepted.find((row) => row.kind === text.kind);
        const expanded = open === text.id;
        return (
          <Panel key={text.id} className={`consent-text tone-${KIND_TONE[text.kind] || 'cyan'} ${mine ? 'is-ticked' : ''}`}>
            <div className="flex flex-wrap items-start gap-3">
              <span className="tile3d__face shrink-0" style={{ width: 44, height: 44 }}>
                <Icon name={KIND_ICON[text.kind] || 'file-text'} size={20} className="tile3d__icon" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-white">{titleOf(text)}</p>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {t('consent.version', { n: text.version })} · {t('consent.publishedOn', { when: formatDate(text.publishedAt) })}
                </p>
                <div className="mt-2">
                  {mine ? (
                    <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="circle-check">
                      {t('consent.acceptedOn', { when: formatDateTime(mine.acceptedAt) })}
                    </Badge>
                  ) : older ? (
                    <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="history">
                      {t('consent.version', { n: older.version })} · {t('consent.acceptedOn', { when: formatDate(older.acceptedAt) })}
                    </Badge>
                  ) : null}
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" className="consent-text__toggle" onClick={() => setOpen(expanded ? null : text.id)} aria-expanded={expanded}>
                  <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={15} />
                  {expanded ? t('consent.hide') : t('consent.read')}
                </button>
                {!mine && (
                  <Button icon="circle-check" loading={busy === text.id} onClick={() => accept(text)}>
                    {t('consent.accept')}
                  </Button>
                )}
              </div>
            </div>
            {text.changeNote && <p className="mt-2 text-xs text-amber-300">{t('consent.whatChanged', { note: text.changeNote })}</p>}
            {expanded && <div className="consent-text__body custom-scrollbar">{bodyOf(text)}</div>}
          </Panel>
        );
      })}
    </div>
  );
}
